import React, { useState, useEffect } from 'react';
import '../../styles/AdminDashboard.scss';
import { cmsAPI, getImageUrl } from '../../utils/api';
import { FaPlus, FaEdit, FaTrash, FaCloudUploadAlt, FaTimes, FaTools } from 'react-icons/fa';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ id: null, title: '', description: '', features: '', tech: '', image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchServices = async () => {
    try {
      const res = await cmsAPI.getServices();
      if (res.data.success) setServices(res.data.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('features', formData.features);
    data.append('tech', formData.tech);
    if (formData.image) data.append('image', formData.image);

    try {
      let res;
      if (isEditing) {
        res = await cmsAPI.updateService(formData.id, data);
      } else {
        res = await cmsAPI.createService(data);
      }

      if (res.data.success) {
        fetchServices();
        resetForm();
        alert(isEditing ? 'Service updated successfully!' : 'Service created successfully!');
      } else {
        alert('Failed to save service');
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('An error occurred while saving.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ id: null, title: '', description: '', features: '', tech: '', image: null });
    setImagePreview(null);
    setIsEditing(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      const res = await cmsAPI.deleteService(id);
      if (res.data.success) {
        fetchServices();
      } else {
        alert('Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleEdit = (service) => {
    setFormData({ 
      id: service.id, 
      title: service.title, 
      description: service.description, 
      features: service.features || '', 
      tech: service.tech || '', 
      image: null 
    });
    setImagePreview(getImageUrl(service.image));
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="admin-dashboard-page">
      <div className="dashboard-header">
        <h1>CMS - Services</h1>
        <p>Manage and customize your website's service offerings</p>
      </div>

      <div className="admin-card">
        <h3>{isEditing ? <><FaEdit /> Edit Service</> : <><FaPlus /> Add New Service</>}</h3>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Service Title</label>
            <input 
              type="text" placeholder="e.g. Enterprise Web Development" required
              value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea 
              placeholder="Tell your clients what this service is about..." required rows="4"
              value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label>Features</label>
              <input 
                type="text" placeholder="Responsive, Secure, SEO Optimized..."
                value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Tech Stack</label>
              <input 
                type="text" placeholder="React, Node.js, MongoDB..."
                value={formData.tech} onChange={(e) => setFormData({...formData, tech: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Service Image / Icon</label>
            <div className="file-input-wrapper">
              <FaCloudUploadAlt size={30} color="#4318ff" />
              <p style={{ margin: '10px 0 0 0', color: '#a3aed0', fontSize: '14px' }}>
                {formData.image ? formData.image.name : 'Click or drag image here to upload'}
              </p>
              <input type="file" accept="image/*" onChange={handleImageChange} />
              
              {imagePreview && (
                <div className="file-preview">
                  <img src={imagePreview} alt="Preview" />
                  <div className="remove-file" onClick={(e) => { e.stopPropagation(); clearImage(); }}>
                    <FaTimes />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : isEditing ? 'Update Service' : 'Create Service'}
            </button>
            {isEditing && (
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="admin-grid">
        {loading ? (
          <p>Loading services...</p>
        ) : services.length > 0 ? services.map(service => (
          <div key={service.id} className="item-card">
            <div className="card-image">
              <img src={getImageUrl(service.image) || 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400"><rect width="100%" height="100%" fill="%230f172a"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="20" fill="%2338bdf8" font-weight="bold">No Image Available</text></svg>'} alt={service.title} />
              <div className="card-badge"><FaTools style={{marginRight: '5px'}}/> Service</div>
            </div>
            <div className="card-body">
              <h4>{service.title}</h4>
              <p>{service.description.length > 120 ? service.description.substring(0, 120) + '...' : service.description}</p>
              <div className="card-footer">
                <button className="btn-edit" onClick={() => handleEdit(service)}><FaEdit /> Edit</button>
                <button className="btn-delete" onClick={() => handleDelete(service.id)}><FaTrash /> Delete</button>
              </div>
            </div>
          </div>
        )) : (
          <div style={{ textAlign: 'center', gridColumn: '1/-1', padding: '40px', color: '#a3aed0' }}>
            <p>No services found. Add your first service above!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminServices;
